import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'

export type Edition = 'full' | 'marketing'

export interface EditionGate {
  /** Resolved product edition; 'full' until settings load (see isLoading). */
  edition: Edition
  /** false on marketing-edition deploys — POS surfaces are hidden and the
   *  backend 403s them (`pos_not_enabled`). */
  posEnabled: boolean
  /** true only while the first fetch is in flight — render neutral UI and
   *  never redirect on an unresolved edition (E-P3 lesson from the
   *  online-ordering gate). */
  isLoading: boolean
  /** true when `['settings','all']` failed — show retry, don't redirect. */
  isError: boolean
}

/**
 * useEdition tells the UI which product edition this deploy serves (backend
 * env `BHOOKLY_EDITION`, surfaced as the server-injected `edition` key on
 * GET /settings — env-authoritative, never stored in app_settings).
 *
 *   • 'full'      → the complete POS (default; a missing key on an old
 *                   backend also reads as full)
 *   • 'marketing' → standalone marketing suite: sidebar hides POS sections,
 *                   the route guard bounces POS paths, and the backend 403s
 *                   (`pos_not_enabled`) every POS API
 *
 * Shares the `['settings','all']` cache — no extra network call.
 */
export function useEdition(): EditionGate {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: Infinity,
    // routes/admin.tsx calls this before its "logged in?" check. Without this
    // guard, opening any /admin URL while logged out fired GET /settings with
    // no token; the 401's retry then sat paused in the shared ['settings',
    // 'all'] entry and every reader after login waited on it (the kitchen
    // manager's Production page said "not switched on"). Disabled, it reads as
    // not-loading 'full' — and the layout redirects to /login anyway.
    enabled: apiClient.isAuthenticated(),
  })
  const all = data?.data as Record<string, unknown> | undefined
  const edition: Edition = all?.edition === 'marketing' ? 'marketing' : 'full'
  return {
    edition,
    posEnabled: edition !== 'marketing',
    isLoading,
    isError,
  }
}
